import React, { useState, useEffect, useRef, useMemo } from "react";
import { Plus, X, BookOpen, Edit2 } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

const API_BASE_URL = "https://backend.dlapakistan.com/api/about/about-dla";

const Aboutdla = () => {
  const [aboutContents, setAboutContents] = useState(() => {
    const savedData = localStorage.getItem("aboutDLA_Storage");
    return savedData ? JSON.parse(savedData) : [];
  });

  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    is_active: true
  });

  const editorRef = useRef(null);
  const contentIdRef = useRef(null); // Keep track of ID separately
  const emerald600 = "#059669";

  // Jodit Editor Configuration - memoized to prevent re-renders
  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com",
    headers: { "Content-Type": "application/json" },
    timeout: 10000, // 10 second timeout
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("access_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const fetchContents = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      console.log("Fetched data:", response.data); // Debug log
      
      if (response.data) {
        // Handle both array and object responses
        let data = response.data;
        
        // If it's an array, use it directly
        if (Array.isArray(data)) {
          if (data.length > 0) {
            localStorage.setItem("aboutDLA_Storage", JSON.stringify(data));
            setAboutContents(data);
            // Store the first item's ID for future updates
            if (data[0]) {
              contentIdRef.current = data[0].id || data[0]._id || data[0].pk || data[0].slug;
              console.log("Stored content ID:", contentIdRef.current);
            }
          }
        } 
        // If it's an object, wrap it in an array
        else if (typeof data === 'object' && data !== null) {
          localStorage.setItem("aboutDLA_Storage", JSON.stringify([data]));
          setAboutContents([data]);
          // Store the ID from the object
          contentIdRef.current = data.id || data._id || data.pk || data.slug;
          console.log("Stored content ID:", contentIdRef.current);
        }
      }
    } catch (error) {
      console.error("Fetch error:", error);
      const savedData = localStorage.getItem("aboutDLA_Storage");
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setAboutContents(parsedData);
          if (parsedData.length > 0 && parsedData[0]) {
            contentIdRef.current = parsedData[0].id || parsedData[0]._id || parsedData[0].pk || parsedData[0].slug;
          }
        } catch (e) {
          console.error("Error parsing saved data:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setEditingContent(null);
    setForm({ title: "", subtitle: "", description: "", is_active: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    // Validate form
    if (!form.title.trim() || !form.subtitle.trim() || !form.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all fields before saving.",
        confirmButtonColor: emerald600
      });
      return;
    }

    try {
      setSaving(true);
      let response;
      
      // Prepare the data to send
      const dataToSend = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        description: form.description.trim(),
        is_active: true
      };
      
      console.log("Current content ID:", contentIdRef.current); // Debug log
      console.log("Editing content:", editingContent); // Debug log
      console.log("Data to send:", dataToSend); // Debug log
      
      // Always check if we have content to update
      if (aboutContents.length > 0) {
        // Get the ID from our ref or from the first item
        let contentId = contentIdRef.current;
        
        if (!contentId && aboutContents[0]) {
          // Try to get ID from the stored content
          contentId = aboutContents[0].id || aboutContents[0]._id || aboutContents[0].pk || aboutContents[0].slug;
          contentIdRef.current = contentId; // Store it for next time
        }
        
        console.log("Using content ID for update:", contentId); // Debug log
        
        if (contentId) {
          // Update existing content
          response = await api.put(`${API_BASE_URL}/${contentId}/`, dataToSend);
          console.log("Update response:", response.data); // Debug log
          
          // Update the stored ID from response if available
          const responseId = response.data.id || response.data._id || response.data.pk || response.data.slug;
          if (responseId) {
            contentIdRef.current = responseId;
          }
          
          // Update local state
          const updatedContents = [response.data];
          setAboutContents(updatedContents);
          setEditingContent(response.data); // Update editingContent with new data
          localStorage.setItem("aboutDLA_Storage", JSON.stringify(updatedContents));
          
          await Swal.fire({ 
            icon: "success", 
            title: "Updated Successfully!", 
            text: "Your content has been updated.",
            showConfirmButton: false, 
            timer: 1500 
          });
          
          closeModal();
          return;
        }
      }
      
      // If we get here, either we have no content or no ID found
      // So create new content
      response = await api.post(API_BASE_URL + "/", dataToSend);
      console.log("Create response:", response.data); // Debug log
      
      // Store the ID from the new content
      const newId = response.data.id || response.data._id || response.data.pk || response.data.slug;
      if (newId) {
        contentIdRef.current = newId;
      }
      
      const newContents = [response.data];
      setAboutContents(newContents);
      setEditingContent(response.data); // Set editingContent with new data
      localStorage.setItem("aboutDLA_Storage", JSON.stringify(newContents));
      
      await Swal.fire({ 
        icon: "success", 
        title: "Created Successfully!", 
        text: "Your content has been saved.",
        showConfirmButton: false, 
        timer: 1500 
      });
      
      closeModal();
      
    } catch (error) {
      console.error("Save error details:", error);
      console.error("Error response:", error.response?.data);
      
      let errorMessage = "Failed to save content. Please try again.";
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = "Invalid data. Please check your inputs.";
          console.error("Validation errors:", error.response.data);
        } else if (error.response.status === 401) {
          errorMessage = "Session expired. Please login again.";
        } else if (error.response.status === 404) {
          errorMessage = "Resource not found. The content may have been deleted. Please try creating new content.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: errorMessage,
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (aboutContents.length > 0) {
      const content = aboutContents[0];
      console.log("Editing content details:", content); // Debug log
      console.log("Content ID fields:", { 
        id: content.id, 
        _id: content._id, 
        pk: content.pk,
        slug: content.slug
      }); // Debug log
      
      setEditingContent(content);
      setForm({
        title: content.title || "",
        subtitle: content.subtitle || "",
        description: content.description || "",
        is_active: true
      });
      setShowModal(true);
    }
  };

  const handleEditorChange = (newContent) => {
    setForm(prev => ({
      ...prev,
      description: newContent
    }));
  };

  if (loading) return <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div></div>;

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">About Management</h1>
            <p className="text-gray-600 mt-1">Manage core DLA content</p>
          </div>
          <button
            onClick={aboutContents.length === 0 ? () => setShowModal(true) : handleEdit}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            {aboutContents.length === 0 ? <><Plus className="w-5 h-5 mr-2" /> Add</> : <><Edit2 className="w-4 h-4 mr-2" /> Edit Content</>}
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="px-8 md:px-12 mt-16 w-full pb-20">
        {aboutContents.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg">No content added yet.</p>
          </div>
        ) : (
          <div className="animate-fadeIn text-left w-full bg-transparent">
            
            <div className="mb-10 border-l-4 pl-6" style={{ borderColor: emerald600 }}>
              <h2 className="text-4xl font-bold text-gray-900 tracking-tight">
                {aboutContents[0]?.title || "No Title"}
              </h2>
            </div>
            
            <div className="w-full mb-10">
              <p className="text-gray-700 text-xl leading-relaxed font-medium italic">
                {aboutContents[0]?.subtitle || "No Subtitle"}
              </p>
            </div>

            {/* DESCRIPTION SECTION */}
            <div className="mt-12 border-t border-gray-200 pt-10 w-full bg-transparent">
              <div 
                className="text-gray-600 text-lg leading-loose space-y-6 custom-description w-full prose prose-emerald max-w-none bg-transparent" 
                dangerouslySetInnerHTML={{ __html: aboutContents[0]?.description || "" }} 
              />
            </div>
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">
                  {aboutContents.length > 0 ? "Update Content" : "Create Content"}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Heading</label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all" 
                    style={{ '--tw-ring-color': emerald600 }} 
                    required 
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Subtitle</label>
                  <textarea 
                    value={form.subtitle} 
                    onChange={(e) => setForm({...form, subtitle: e.target.value})} 
                    rows="3" 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 transition-all resize-none" 
                    style={{ '--tw-ring-color': emerald600 }} 
                    required 
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">Description</label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.description}
                      config={config}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== form.description) {
                          setForm(prev => ({...prev, description: newContent}));
                        }
                      }}
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : aboutContents.length > 0 ? (
                    'Update Content'
                  ) : (
                    'Create Content'
                  )}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-modalIn { animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        
        /* Remove background and borders from rich text output */
        .custom-description * { background-color: transparent !important; border: none !important; }
        .custom-description ul { list-style-type: disc; padding-left: 1.5rem; }
        .custom-description ol { list-style-type: decimal; padding-left: 1.5rem; }
        
        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 300px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #eee !important;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 16px !important;
          min-height: 200px;
        }
      `}</style>
    </div>
  );
};

export default Aboutdla;